// @ts-nocheck
// Remove if react-quill is not used
import "react-quill/dist/quill.snow.css";
// Remove if react-draft-wysiwyg is not used
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
// Remove if simplebar is not used
import "simplebar-react/dist/simplebar.min.css";
// Remove if mapbox is not used
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import "mapbox-gl/dist/mapbox-gl.css";
import { FC, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Provider as ReduxProvider } from "react-redux";
import { useRoutes } from "react-router-dom";
import { usePathname } from "src/hooks/use-pathname";

import { CookieBanner } from "src/components/cookie-banner";
import { MaintenanceBanner } from "src/components/maintenance-banner";
import { RTL } from "src/components/rtl";
import { SettingsButton } from "src/components/settings/settings-button";
import { SettingsDrawer } from "src/components/settings/settings-drawer";
import { SplashScreen } from "src/components/splash-screen";
import { Toaster } from "src/components/toaster";
import { AuthConsumer, AuthProvider } from "src/contexts/auth/firebase-context";
import { CookieConsentProvider } from "src/contexts/cookie-consent-context";
import { EnvVarsProvider } from "src/contexts/envvars-context";
import { GlucoseProvider } from "src/contexts/glucose-context";
import {
  SettingsConsumer,
  SettingsProvider,
} from "src/contexts/settings-context";
import { SocketProvider } from "src/contexts/socket-context";
import { useNprogress } from "src/hooks/use-nprogress";
import "src/locales/i18n";
import { routes } from "src/routes";
import { store } from "src/store";
import { createTheme } from "src/theme";
import { rememberLastPage } from "src/utils/last-page";
import i18next from "./i18n";

export const App: FC = () => {
  // get saved language from local storage
  const savedLanguage = localStorage.getItem("language");
  // get browser language
  const browserLanguage = navigator.language.split("-")[0];
  // set language to saved language or browser language
  const language = savedLanguage || browserLanguage;
  // set language in i18n
  useEffect(() => {
    localStorage.setItem("language", language);
    i18next.changeLanguage(language);
  }, [language]);

  useNprogress();

  const element = useRoutes(routes);
  const path = usePathname();

  useEffect(() => {
    rememberLastPage(path);
  }, [path]);

  const isMainPage = path === "/";
  const isAuthPage = path.includes("/auth");
  const isBlog = path.includes("/blog");
  const isLegalNotice = path.includes("/legalnotice");
  const isPrivacyPolicy = path.includes("/privacy-policy");
  const aboutUs = path.includes("/aboutus");
  const isWorkshop = path.includes("/workshop");
  const isDontShowSettings = false;
  // isMainPage ||
  // isAuthPage ||
  // isBlog ||
  // isLegalNotice ||
  // aboutUs ||
  // isWorkshop ||
  // isPrivacyPolicy;

  return (
    <ReduxProvider store={store}>
      <CookieConsentProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AuthProvider>
          <SocketProvider>
            <AuthConsumer>
              {(auth) => (
                <SettingsProvider>
                  <SettingsConsumer>
                    {(settings) => {
                      // Prevent theme flicker when restoring custom settings from browser storage
                      if (!settings.isInitialized) {
                        // return null;
                      }

                      const theme = createTheme({
                        colorPreset: settings.colorPreset,
                        contrast: settings.contrast,
                        direction: settings.direction,
                        paletteMode: settings.paletteMode,
                        responsiveFontSizes: settings.responsiveFontSizes,
                      });

                      // Prevent guards from redirecting
                      const showSlashScreen = !auth.isInitialized;

                      return (
                        <ThemeProvider theme={theme}>
                          <Helmet>
                            <meta
                              name="color-scheme"
                              content={settings.paletteMode}
                            />
                            <meta
                              name="theme-color"
                              content={theme.palette.neutral[900]}
                            />
                          </Helmet>
                          <RTL direction={settings.direction}>
                            <CssBaseline />
                            {showSlashScreen ? (
                              <SplashScreen />
                            ) : (
                              <>
                                {element}
                                {!isDontShowSettings && (
                                  <SettingsButton
                                    onClick={settings.handleDrawerOpen}
                                  />
                                )}
                                {!isDontShowSettings && (
                                  <SettingsDrawer
                                    canReset={settings.isCustom}
                                    onClose={settings.handleDrawerClose}
                                    onReset={settings.handleReset}
                                    onUpdate={settings.handleUpdate}
                                    open={settings.openDrawer}
                                    values={{
                                      colorPreset: settings.colorPreset,
                                      contrast: settings.contrast,
                                      direction: settings.direction,
                                      paletteMode: settings.paletteMode,
                                      responsiveFontSizes:
                                        settings.responsiveFontSizes,
                                      stretch: settings.stretch,
                                      layout: settings.layout,
                                      navColor: settings.navColor,
                                    }}
                                  />
                                )}
                              </>
                            )}
                            {/* <MaintenanceBanner /> */}
                            <CookieBanner />
                            <Toaster />
                          </RTL>
                        </ThemeProvider>
                      );
                    }}
                  </SettingsConsumer>
                </SettingsProvider>
              )}
            </AuthConsumer>
          </SocketProvider>
        </AuthProvider>
      </LocalizationProvider>
      </CookieConsentProvider>
    </ReduxProvider>
  );
};
